import { forEach, indexOf } from "lodash-es"
import * as Cookies from "tiny-cookie"
import { useStore } from "../stores"
import { useUserStore } from "../stores/user"
import userAxios from "../includes/axios"

function isValidLanguage(language: string, supportedLanguages: { key: string }[]): string | false
{
	for (let index = 0; index < supportedLanguages.length; index++) {
		if(language == supportedLanguages[index].key) return supportedLanguages[index].key
	}

	return false
}

export function getSuppotedBrowserLanguages()
{
	return navigator.languages.map(lang => lang.split("-")[0])
}

/**
 * Return language if detected or false if not found
 * @param supportedLanguages 
 * @param path 
 * @returns 
 */
export function isLanguageInPath(supportedLanguages: { key: string }[], path: string)
{
	return isValidLanguage(path.split('/')[1], supportedLanguages)
}

/**
 * Detect language from URL, cookie or browser settings
 * 
 * @param {Array} supportedLanguages - list of supported languages
 * @param {String} defaultLanguage - default language
 * @returns {String} - detected language
 */
export function detectLanguage(supportedLanguages: { key: string }[], defaultLanguage: string): string | false
{
	// Try to figure out if we have language in the URL
	let language = isLanguageInPath(supportedLanguages, window.location.pathname)
	
	if(language) return language;
	
	// If nothing in URL lets look into cookie value
	language = Cookies.get('language') || ''
	if(language && isValidLanguage(language, supportedLanguages)) return language;
	
	// If nothing - autodetect from list of supported languages
	const browserLangs = getSuppotedBrowserLanguages()
	
	// Find first supported language in browser languages
	supportedLanguages.some(item => {
		if(indexOf(browserLangs, item.key) >= 0){
			language = item.key
			return true;
		}
	})

	return language || defaultLanguage
}

/**
 * Set language to the cookie
 * @param language 
 * @returns 
 */
export function setLanguageCookie(language: string)
{
	return Cookies.set('language', language, {
		expires: '3Y',
		samesite: 'strict',
		secure: true
	})
}

export function setLanguage(language: string)
{
	// Save detected language to the store
	const store = useStore()

	store.setCurrentLanguage(language)
	setLanguageCookie(language)

	return language
}

export function setupLanguage(): void
{
	const store = useStore()
	if(!store.settings.defaultLanguage) return;

	const language = detectLanguage(store.settings.languagesSupported, store.settings.defaultLanguage);
	if(language) setLanguage(language)
}

export function personNameFormalize(language: string, nameString: string)
{
	if(language != 'lt') return nameString;

	const suffixes = {
		'a': 'a',
		'as': 'ai',
		'ė': 'e',
		'is': 'i',
		'us': 'au',
		'ys': 'y'
	}

	// Parse names. There could be more than one
	const names = nameString.trim().split(" ")
	const output: string[] = []

	// Replace every ending of the name according suffixes map
	names.forEach(str => {
		forEach(suffixes, (to: string, from: string) => {
			if(str.endsWith(from)){
				str = str.replace(new RegExp(from + "$"), to)
			}
		})

		output.push(str)
	})
	
	return output.join(" ")
}

export function switchLanguage(language: string){
	const store = useStore()
	const userStore = useUserStore()

	// Do nothing it's an attempt to switch to the current language
	if(store.currentLanguage == language){
		return; 
	}

	const languageFrom = store.currentLanguage

	store.loaderContent = "default"
	setLanguage(language)
	
	setTimeout(async () => { // Delay execution to let transitions get in force
		// Save new language to cookies
		
		// Save language to server if needed and reload the page
		if(userStore.loggedIn){
			await userAxios.post('/api/user-side/saveLanguage', {
				language
			})
		}
		
		if(location.pathname.substring(0, 3) == ("/" + languageFrom)){
			// Redirect to an URL with a new language in URL
			location.href = location.pathname.replace("/" + languageFrom, "/" + language) + location.search
		}
	}, 500)
}

// Fetch translation JSON from server and set it to the store
export async function load(){
	const store = useStore()
	
	const { data } = await userAxios.get(`/api/user-side/getTranslations/${store.currentLanguage}`)
	store.translations = data
}